<template>
  <v-container fluid class="toppings-page">
    <!-- TOP HEADER -->
    <div class="ps-page-header d-flex align-center justify-space-between flex-wrap mb-4" style="gap: 16px;">
      <div class="d-flex align-center" style="gap: 12px;">
        <span class="ap-card-accent"></span>
        <div>
          <div class="d-flex align-center" style="gap: 8px;">
            <h1 class="ps-page-title mb-0">{{ $t("toppings") }}</h1>
            <v-chip x-small color="primary" outlined class="font-weight-bold">
              {{ toppingsTotal }} {{ $t("items") || "items" }}
            </v-chip>
          </div>
          <div class="ps-page-subtitle">{{ $t("topping_page_subtitle") }}</div>
        </div>
      </div>

      <div v-if="canManage" class="d-flex align-center" style="gap: 10px;">
        <v-btn
          depressed
          color="primary"
          class="white--text font-weight-bold text-none rounded-lg px-4"
          @click="newItem"
        >
          <v-icon left small>fal fa-plus-circle</v-icon>
          {{ $t("new_data") }}
        </v-btn>
      </div>
    </div>

    <!-- METRICS KPI STRIP -->
    <KpiCards class="mb-4" :items="kpiItems" :loading="loadingToppings" :skeleton="3" />

    <!-- MAIN TABLE CARD -->
    <v-card class="ap-card" outlined elevation="0">
      <!-- Toolbar: Search + Status Filter Tabs -->
      <div class="py-3 px-4 border-bottom d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
        <div class="d-flex align-center flex-grow-1" style="max-width: 420px; min-width: 260px;">
          <TextSearch
            v-model="search"
            :placeholder="$t('search')"
            @search="fetchItem()"
            @onClear="
              search = '';
              fetchItem();
            "
          />
        </div>

        <!-- Segmented Status Tabs (Matches /manage-waste .br-tabs style) -->
        <div class="br-tabs">
          <div
            v-for="(item, i) in statusList"
            :key="i"
            class="br-tab"
            :class="{ 'br-tab--active': status_filter === item.value }"
            @click="status_filter = item.value"
          >
            {{ $t(item.text) }}
          </div>
        </div>
      </div>

      <!-- Content Area -->
      <div class="toppings-table-container">
        <v-data-table
          :headers="customHeaders"
          :items="toppingItems"
          :loading="loadingToppings"
          hide-default-footer
          class="purchase-table elevation-0"
          :items-per-page="50"
        >
          <!-- Row index -->
          <template slot="item.index" slot-scope="{ index }">
            <span class="ps-row-index">{{ index + 1 }}</span>
          </template>

          <!-- Topping Info: Avatar, Names, Subtitle -->
          <template slot="item.name" slot-scope="{ item }">
            <div class="d-flex align-center py-2" style="cursor: pointer;" @click="viewItem(item)">
              <span class="ps-row-thumb mr-3 shrink-0">
                <v-avatar tile size="40" class="rounded-lg ps-thumb-avatar">
                  <app-img v-if="item.img" :img="item.img" />
                  <v-icon v-else size="22" color="#94a3b8">fal fa-cookie-bite</v-icon>
                </v-avatar>
              </span>
              <div class="ps-row-name min-w-0">
                <span class="ps-product-name text-truncate" :title="toppingName(item)">
                  {{ toppingName(item) }}
                </span>
                <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                  <span v-if="secondaryName(item)" class="text-caption grey--text text-truncate">
                    {{ secondaryName(item) }}
                  </span>
                  <span v-if="item.id" class="ps-product-code">#{{ item.id }}</span>
                </div>
              </div>
            </div>
          </template>

          <!-- Price -->
          <template slot="item.price" slot-scope="{ item }">
            <span class="buy-price-val">
              +{{ numberText(item.price) }}
              <span class="text-caption grey--text">{{ item.currency_code || "₭" }}</span>
            </span>
          </template>

          <!-- Cost -->
          <template slot="item.cost" slot-scope="{ item }">
            <span class="text-caption font-weight-medium grey--text text--darken-2">
              {{ numberText(item.cost) }} {{ item.currency_code || "₭" }}
            </span>
          </template>

          <!-- Recipe / Ingredients -->
          <template slot="item.ingredients" slot-scope="{ item }">
            <div v-if="item.ingredient_names" class="d-flex align-center" style="gap: 4px;">
              <v-chip x-small label outlined color="primary" class="font-weight-medium">
                <v-icon left x-small>fal fa-mortar-pestle</v-icon>
                {{ item.ingredient_names }}
              </v-chip>
            </div>
            <span v-else class="text-caption grey--text">
              {{ $t("no_ingredient_linked") }}
            </span>
          </template>

          <!-- Status Pill -->
          <template slot="item.status" slot-scope="{ item }">
            <span
              class="stock-pill"
              :class="item.is_active ? 'stock-pill-normal' : 'stock-pill-out'"
            >
              {{ item.is_active ? $t("active") : $t("inactive") }}
            </span>
          </template>

          <!-- Actions -->
          <template slot="item.actions" slot-scope="{ item }">
            <div class="d-flex align-center justify-center" style="gap: 6px;">
              <button
                type="button"
                class="ps-act"
                :title="$t('view')"
                @click="viewItem(item)"
              >
                <v-icon x-small>fal fa-eye</v-icon>
              </button>
              <button
                v-if="canManage"
                type="button"
                class="ps-act"
                :title="$t('edit')"
                @click="editItem(item)"
              >
                <v-icon x-small>fal fa-pen</v-icon>
              </button>
              <button
                v-if="canManage"
                type="button"
                class="ps-act ps-act--danger"
                :title="$t('delete')"
                @click="deleteItem(item)"
              >
                <v-icon x-small>fal fa-trash-alt</v-icon>
              </button>
            </div>
          </template>

          <!-- Empty State -->
          <template slot="no-data">
            <div class="ps-empty text-center py-10">
              <v-icon size="48" class="ps-empty-icon mb-2">fal fa-cookie-bite</v-icon>
              <div class="ps-empty-text font-weight-bold">
                {{ $t("no_data") }}
              </div>
            </div>
          </template>
        </v-data-table>
      </div>

      <!-- Pagination Footer -->
      <div
        v-if="toppings && toppings.last_page > 1"
        class="py-3 px-4 border-top d-flex align-center justify-space-between flex-wrap"
        style="gap: 12px;"
      >
        <div class="text-caption grey--text">
          {{ pageRangeText }}
        </div>
        <v-pagination
          v-model="currentPage"
          :length="toppings.last_page"
          :total-visible="7"
          circle
          color="primary"
          class="ps-pagination"
          @input="onPageChange"
        />
      </div>
    </v-card>

    <!-- VIEW TOPPING DETAIL DIALOG (PosSmart Brutal Style) -->
    <app-custom-dialog v-model="dialog" :footer="false" title="description" width="560px">
      <div slot="activator" class="topping-detail pa-2">
        <div class="d-flex align-center justify-space-between mb-4 border-bottom pb-3">
          <div class="d-flex align-center" style="gap: 12px;">
            <v-avatar tile size="56" class="rounded-xl ps-thumb-avatar">
              <app-img v-if="form.img" :img="form.img" />
              <v-icon v-else size="28" color="#94a3b8">fal fa-cookie-bite</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-bold text-subtitle-1">{{ form.name_la || "-" }}</div>
              <div v-if="form.name_en" class="text-caption grey--text">{{ form.name_en }}</div>
            </div>
          </div>
          <span
            class="stock-pill"
            :class="form.is_active ? 'stock-pill-normal' : 'stock-pill-out'"
          >
            {{ form.is_active ? $t("active") : $t("inactive") }}
          </span>
        </div>

        <!-- Prices Strip -->
        <v-row dense class="mb-3">
          <v-col cols="6">
            <div class="ps-summary-card">
              <div class="ps-summary-icon ps-summary-icon--primary">
                <v-icon small color="primary">fal fa-tag</v-icon>
              </div>
              <div class="ps-summary-meta">
                <div class="ps-summary-label">{{ $t("topping_price") }}</div>
                <div class="ps-summary-value">{{ numberText(form.price) }} {{ form.currency_code || "₭" }}</div>
              </div>
            </div>
          </v-col>
          <v-col cols="6">
            <div class="ps-summary-card">
              <div class="ps-summary-icon ps-summary-icon--info">
                <v-icon small color="info">fal fa-coins</v-icon>
              </div>
              <div class="ps-summary-meta">
                <div class="ps-summary-label">{{ $t("topping_cost") }}</div>
                <div class="ps-summary-value ps-summary-value--info">{{ numberText(form.cost) }} {{ form.currency_code || "₭" }}</div>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Multi-language names list -->
        <div class="ap-card pa-3 mb-3 bg-slate-50">
          <div class="text-caption font-weight-bold grey--text text-uppercase mb-2">{{ $t("name") }}</div>
          <div class="d-flex flex-column" style="gap: 6px;">
            <div class="d-flex justify-space-between text-body-2">
              <span class="grey--text">{{ $t("name_la") || "Lao" }}:</span>
              <span class="font-weight-medium">{{ form.name_la || "-" }}</span>
            </div>
            <div v-if="form.name_en" class="d-flex justify-space-between text-body-2">
              <span class="grey--text">{{ $t("name_en") || "English" }}:</span>
              <span class="font-weight-medium">{{ form.name_en }}</span>
            </div>
            <div v-if="form.name_th" class="d-flex justify-space-between text-body-2">
              <span class="grey--text">{{ $t("name_th") || "Thai" }}:</span>
              <span class="font-weight-medium">{{ form.name_th }}</span>
            </div>
            <div v-if="form.name_cn" class="d-flex justify-space-between text-body-2">
              <span class="grey--text">{{ $t("name_cn") || "Chinese" }}:</span>
              <span class="font-weight-medium">{{ form.name_cn }}</span>
            </div>
          </div>
        </div>

        <!-- Ingredients recipe -->
        <div class="ap-card pa-3 bg-slate-50">
          <div class="text-caption font-weight-bold grey--text text-uppercase mb-2">{{ $t("ingredient") }}</div>
          <div v-if="!form.ingredients || !form.ingredients.length" class="text-caption grey--text">
            {{ $t("no_ingredient_linked") }}
          </div>
          <div v-else class="d-flex flex-column" style="gap: 6px;">
            <div
              v-for="(line, idx) in form.ingredients"
              :key="idx"
              class="d-flex justify-space-between align-center text-body-2 py-1 border-bottom"
            >
              <span class="font-weight-medium">{{ line.name_la || line.name_en }}</span>
              <span class="grey--text text-caption font-weight-bold">
                {{ numberText(line.quantity) }} {{ line.unit_name }} ({{ numberText(line.cost) }} ₭)
              </span>
            </div>
          </div>
        </div>
      </div>
    </app-custom-dialog>

    <app-loading :overlay="overlay" />
  </v-container>
</template>

<script>
import { confirm, success } from "@/controllers/alert";
import { hasActionPermission } from "@/router/middleware/permission";
import { mapGetters } from "vuex";

export default {
  name: "ManageToppingPage",
  data: () => ({
    overlay: false,
    loadingToppings: true,
    hasLoadedToppings: false,
    dialog: false,
    search: "",
    status_filter: null,
    currentPage: 1,
    form: {
      id: null,
      name_la: "",
      name_en: "",
      name_th: "",
      name_cn: "",
      img: null,
      ingredients: [],
      cost: 0,
      price: 0,
      currency_code: null,
      is_active: true,
    },
  }),
  async created() {
    await this.fetchItem();
  },
  computed: {
    kpiItems() {
      return [
        {
          key: "total",
          label: this.$t("total") || "Total Toppings",
          icon: "fal fa-cookie-bite",
          iconColor: "primary",
          tone: "primary",
          value: this.toppingsTotal,
        },
        {
          key: "active",
          label: this.$t("active") || "Active",
          icon: "fal fa-check-circle",
          iconColor: "success",
          tone: "success",
          value: this.activeToppingsCount,
        },
        {
          key: "inactive",
          label: this.$t("inactive") || "Inactive",
          icon: "fal fa-ban",
          iconColor: "error",
          tone: "danger",
          value: this.inactiveToppingsCount,
        },
      ];
    },
    ...mapGetters({
      toppings: "index/toppings",
      user: "auth/user",
    }),
    canManage() {
      return (
        hasActionPermission(this.user, "manage_topping") ||
        hasActionPermission(this.user, "manage_product_recipe")
      );
    },
    toppingsTotal() {
      return this.toppings?.total || (this.toppings?.data || []).length;
    },
    activeToppingsCount() {
      const list = this.toppings?.data || [];
      return list.filter((t) => Number(t.is_active) === 1).length;
    },
    inactiveToppingsCount() {
      const list = this.toppings?.data || [];
      return list.filter((t) => Number(t.is_active) === 0).length;
    },
    toppingItems() {
      return (this.toppings?.data || []).map(this.mapToppingForTable);
    },
    customHeaders() {
      return [
        { text: "#", value: "index", align: "center", sortable: false, width: "50px" },
        { text: this.$t("name"), value: "name", sortable: false },
        { text: this.$t("topping_price"), value: "price", align: "right", sortable: false, width: "130px" },
        { text: this.$t("topping_cost"), value: "cost", align: "right", sortable: false, width: "120px" },
        { text: this.$t("ingredient"), value: "ingredients", sortable: false, width: "200px" },
        { text: this.$t("status"), value: "status", align: "center", sortable: false, width: "110px" },
        { text: this.$t("manage"), value: "actions", align: "center", sortable: false, width: "120px" },
      ];
    },
    statusList() {
      return [
        { value: null, text: "all" },
        { value: 1, text: "active" },
        { value: 0, text: "inactive" },
      ];
    },
    pageRangeText() {
      if (!this.toppingsTotal) return "0 of 0";
      const perPage = this.toppings?.per_page || 15;
      const start = (this.currentPage - 1) * perPage + 1;
      const end = Math.min(start + (this.toppings?.data || []).length - 1, this.toppingsTotal);
      return `${start}-${end} of ${this.toppingsTotal}`;
    },
  },
  watch: {
    status_filter() {
      this.currentPage = 1;
      this.fetchItem();
    },
  },
  methods: {
    onPageChange(page) {
      this.currentPage = page;
      this.fetchItem({ page });
    },
    async fetchItem(pagination = {}) {
      const page = pagination.page || this.currentPage;
      const params = new URLSearchParams();
      params.append("page", page);
      if (this.search) params.append("q", this.search);
      if (this.status_filter !== null && this.status_filter !== undefined) {
        params.append("is_active", this.status_filter);
      }
      const requestUrl = `toppings_paginate?${params.toString()}`;

      try {
        this.loadingToppings = true;
        const { data } = await this.$http.get(requestUrl);
        if (data.status) {
          this.$store.commit("index/SET_TOPPINGS", data.data);
          this.hasLoadedToppings = true;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingToppings = false;
      }
    },
    newItem() {
      this.$router.push("/manage-topping/new");
    },
    editItem(item) {
      this.$router.push(`/manage-topping/${item.id}`);
    },
    viewItem(item) {
      this.form = this.mapToppingToForm(item);
      this.dialog = true;
    },
    async deleteItem(item) {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_delete",
      });
      if (!isConfirmed) return;

      this.overlay = true;
      try {
        const { data } = await this.$http.delete(`toppings/${item.id}`);
        if (data.status) {
          await this.fetchItem();
          await success(this, data.message);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.overlay = false;
      }
    },
    mapToppingForTable(item) {
      return {
        ...item,
        topping_name: this.toppingName(item),
        ingredient_name_display: item.ingredient_names || this.$t("no_ingredient_linked"),
        cost_display: this.numberText(item.cost),
        price_display: item.currency_code
          ? `${this.numberText(item.price)} ${item.currency_code}`
          : this.numberText(item.price),
        active_label: item.is_active ? this.$t("active") : this.$t("inactive"),
      };
    },
    mapToppingToForm(item = {}) {
      return {
        id: item.id || null,
        name_la: item.name_la || "",
        name_en: item.name_en || "",
        name_th: item.name_th || "",
        name_cn: item.name_cn || "",
        img: item.img || null,
        ingredients: item.ingredients || [],
        cost: item.cost || 0,
        price: item.price || 0,
        currency_code: item.currency_code || null,
        is_active: Number(item.is_active) === 1,
      };
    },
    toppingName(item) {
      const locale = this.$i18n.locale;
      if (locale === "en" && item.name_en) return item.name_en;
      if (locale === "th" && item.name_th) return item.name_th;
      if ((locale === "cn" || locale === "ch") && item.name_cn) return item.name_cn;
      return item.name_la || item.name_en || item.name_th || item.name_cn || "-";
    },
    secondaryName(item) {
      const locale = this.$i18n.locale;
      if (locale === "la") {
        return item.name_en || item.name_th || "";
      }
      return item.name_la || "";
    },
    numberText(value) {
      const number = value === null || value === undefined || value === "" ? 0 : Number(value);
      return number.toLocaleString();
    },
  },
};
</script>

<style scoped>
.toppings-page {
  max-width: 1400px;
  margin: 0 auto;
}

.ps-page-title {
  font-size: 20px;
  font-weight: 800;
  color: #141021;
  letter-spacing: -0.01em;
}

.ps-page-subtitle {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 2px;
}

/* Card Shell */
.ap-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  overflow: hidden;
  background-color: #ffffff !important;
}

.ap-card-accent {
  width: 3px;
  height: 18px;
  border-radius: 2px;
  background: #5b39c9;
  display: inline-block;
  flex-shrink: 0;
}

.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}

.border-top {
  border-top: 1px solid #e2e8f0 !important;
}

.bg-slate-50 {
  background-color: #f8fafc !important;
}

/* PosSmart Segmented Tabs */
.br-tabs {
  display: inline-flex;
  height: 38px;
  box-sizing: border-box;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 10px;
  background: #f1f2f7;
  border: 1px solid #e2e8f0;
}

.br-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 4px 14px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  color: #64748b;
  transition: all 0.15s ease;
}

.br-tab:hover {
  color: #141021;
}

.br-tab--active {
  color: #5b39c9 !important;
  background: #ffffff !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  font-weight: 700;
}

/* PosSmart Summary Metrics Cards */
.ps-summary-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  transition: all 0.15s ease;
}

.ps-summary-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.ps-summary-icon {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
}

.ps-summary-icon--primary {
  background: rgba(91, 57, 201, 0.1);
  color: #5b39c9;
  border-color: rgba(91, 57, 201, 0.2);
}

.ps-summary-icon--danger {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecdd3;
}

.ps-summary-icon--info {
  background: #f0fdf4;
  color: #10b981;
  border-color: #a7f3d0;
}

.ps-summary-meta {
  min-width: 0;
  flex: 1 1 auto;
}

.ps-summary-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
}

.ps-summary-value {
  font-size: 18px;
  font-weight: 800;
  color: #1e293b;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.ps-summary-value--danger {
  color: #dc2626;
}

.ps-summary-value--info {
  color: #10b981;
}

/* Table Styling (matches purchase.vue & waste-management.vue) */
.toppings-table-container {
  overflow-x: auto;
}

.purchase-table >>> .v-data-table__wrapper table {
  border-collapse: collapse;
}

.purchase-table >>> .v-data-table__wrapper table thead th {
  color: #64748b;
  font-weight: 700;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
  border-bottom: 1px solid #e2e8f0;
  height: 42px;
  white-space: nowrap;
}

.purchase-table >>> .v-data-table__wrapper table tbody tr {
  transition: background-color 0.15s ease;
}

.purchase-table >>> .v-data-table__wrapper table tbody tr:hover {
  background-color: #f8fafc !important;
}

.purchase-table >>> .v-data-table__wrapper table tbody td {
  border-bottom: 1px solid #f1f5f9;
  padding: 10px 14px;
}

.ps-row-index {
  font-size: 12px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}

.ps-row-thumb {
  position: relative;
  width: 40px;
  height: 40px;
  display: inline-flex;
}

.ps-thumb-avatar {
  background: #f1f5f9 !important;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.ps-row-name {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ps-product-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-product-code {
  display: inline-flex;
  align-items: center;
  font-size: 10.5px;
  font-family: monospace;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 0 4px;
  border-radius: 4px;
}

.buy-price-val {
  font-size: 13.5px;
  font-weight: 700;
  color: #5b39c9;
  font-variant-numeric: tabular-nums;
}

/* Stock / Status Pill */
.stock-pill {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 600;
  white-space: nowrap;
  display: inline-block;
}

.stock-pill-out {
  background: #fef2f2;
  color: #ef4444;
}

.stock-pill-normal {
  background: #f0fdf4;
  color: #16a34a;
}

/* Action button */
.ps-act {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ps-act:hover {
  color: #5b39c9;
  border-color: #5b39c9;
  background: rgba(91, 57, 201, 0.06);
}

.ps-act--danger:hover {
  color: #ef4444;
  border-color: #ef4444;
  background: #fef2f2;
}

.ps-empty-icon {
  color: #cbd5e1 !important;
}

.ps-empty-text {
  font-size: 14px;
  color: #475569;
}

/* Dark Theme Overrides */
.theme--dark .ps-page-title {
  color: #ffffff;
}

.theme--dark .ps-page-subtitle {
  color: #94a3b8;
}

.theme--dark .ap-card {
  border-color: rgba(255, 255, 255, 0.08) !important;
  background-color: #1e1e2d !important;
}

.theme--dark .border-bottom {
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .border-top {
  border-top-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .bg-slate-50 {
  background-color: #262638 !important;
}

.theme--dark .br-tabs {
  background: #1b1d30;
  border-color: rgba(154, 140, 240, 0.16);
}

.theme--dark .br-tab {
  color: #7c82a0;
}

.theme--dark .br-tab--active {
  color: #9a8cf0 !important;
  background: rgba(154, 140, 240, 0.18) !important;
}

.theme--dark .ps-summary-card {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .ps-summary-card:hover {
  border-color: rgba(255, 255, 255, 0.16);
}

.theme--dark .ps-summary-icon--primary {
  background: rgba(129, 140, 248, 0.15);
  color: #a5b4fc;
  border-color: rgba(129, 140, 248, 0.25);
}

.theme--dark .ps-summary-icon--danger {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
  border-color: rgba(239, 68, 68, 0.25);
}

.theme--dark .ps-summary-icon--info {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
  border-color: rgba(16, 185, 129, 0.25);
}

.theme--dark .ps-summary-value {
  color: #ffffff;
}

.theme--dark .purchase-table >>> .v-data-table__wrapper table thead th {
  color: #94a3b8;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.theme--dark .purchase-table >>> .v-data-table__wrapper table tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.03) !important;
}

.theme--dark .purchase-table >>> .v-data-table__wrapper table tbody td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  color: #f1f5f9;
}

.theme--dark .ps-thumb-avatar {
  background: #262638 !important;
  border-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .ps-product-name {
  color: #f1f5f9;
}

.theme--dark .ps-product-code {
  background: #262638;
  color: #94a3b8;
}

.theme--dark .buy-price-val {
  color: #a5b4fc;
}

.theme--dark .stock-pill-out {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.theme--dark .stock-pill-normal {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
}

.theme--dark .ps-act {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.1);
  color: #94a3b8;
}

.theme--dark .ps-act:hover {
  color: #a5b4fc;
  border-color: #818cf8;
  background: rgba(129, 140, 248, 0.15);
}

.theme--dark .ps-act--danger:hover {
  color: #f87171;
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.15);
}

.theme--dark .ps-empty-icon {
  color: #475569 !important;
}

.theme--dark .ps-empty-text {
  color: #cbd5e1;
}
</style>
